import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { CandidateCard, type Comparability } from '@/components/recovery/CandidateCard'
import { DecisionHistory } from '@/components/recovery/DecisionHistory'
import { SubjectStake } from '@/components/recovery/SubjectStake'
import {
  getRecoveryDecisionHistory,
  getRecoverySubject,
  listRecoverySubjects,
  recordRecoveryDecision,
  type RecoveryDecisionRequest,
} from '@/lib/api/client/product-recovery'
import type {
  RecoveryAuditDecision,
  RecoveryBasis,
  RecoveryDecision,
  RecoveryOutcome,
  RecoveryStatusFilter,
  RecoverySubjectDetail,
  RecoverySubjectPage,
} from '@/lib/api/schemas/product-recovery.schema'
import {
  ABSENT_FACT,
  basisLabels,
  outcomeLabels,
  subjectReasonText,
  subjectStatusLabels,
} from '@/lib/recovery/labels'
import { noRecordedFactSeparates, rankCandidates } from '@/lib/recovery/ranking'

const PAGE_SIZE = 50

type StatusFilter = { label: string; value: RecoveryStatusFilter }

// A decided subject is not deleted. `cannot_determine` is a settled outcome that
// leaves the row here, and `held` is a row the server refuses a decision for
// until the owner releases it, so each gets its own chip. `all` exists because
// the contract has it, and is never the default the screen opens on.
const STATUS_FILTERS: readonly StatusFilter[] = [
  { label: 'Ждёт решения', value: 'open' },
  { label: 'Ожидает решения владельца', value: 'held' },
  { label: 'Определить не удалось', value: 'cannot_determine' },
  { label: 'Решение принято', value: 'closed' },
  { label: 'Все', value: 'all' },
]

type Feedback = { kind: 'error' | 'info'; text: string }

const OUTCOMES: readonly RecoveryOutcome[] = ['matched', 'not_any_of_these', 'cannot_determine']
const BASES: readonly RecoveryBasis[] = ['exact_source_identity', 'operator_recognition']

/** A recorded decision, with its basis named rather than folded into a verdict. */
function DecisionSummary({ decision }: { decision: RecoveryDecision }) {
  return (
    <p
      className="mt-3 rounded-md border border-gold/40 bg-gold/5 px-3 py-2 text-xs text-ink-2"
      data-testid="recovery-decision"
      data-outcome={decision.outcome}
      data-basis={decision.basis ?? undefined}
    >
      {outcomeLabels[decision.outcome]}
      {decision.basis === null ? null : <> · Основание: {basisLabels[decision.basis]}</>}
      {' · '}{decision.decidedBy}
      {decision.evidenceNote === null || decision.evidenceNote === '' ? null : <> · {decision.evidenceNote}</>}
    </p>
  )
}

export default function PieceRecoveryPage() {
  const [statusFilter, setStatusFilter] = useState<RecoveryStatusFilter>('open')
  const [page, setPage] = useState<RecoverySubjectPage | null>(null)
  const [offset, setOffset] = useState(0)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<RecoverySubjectDetail | null>(null)
  const [history, setHistory] = useState<readonly RecoveryAuditDecision[] | null>(null)
  const [actor, setActor] = useState('')
  const [outcome, setOutcome] = useState<RecoveryOutcome | null>(null)
  const [targetProductId, setTargetProductId] = useState<string | null>(null)
  const [basis, setBasis] = useState<RecoveryBasis | null>(null)
  const [note, setNote] = useState('')
  const [recorded, setRecorded] = useState<RecoveryDecision | null>(null)
  const [feedback, setFeedback] = useState<Feedback | null>(null)
  const [busy, setBusy] = useState(false)

  const loadQueue = useCallback(async (
    nextOffset: number,
    status: RecoveryStatusFilter,
  ): Promise<RecoverySubjectPage | null> => {
    try {
      const loaded = await listRecoverySubjects({ limit: PAGE_SIZE, offset: nextOffset, status })
      setPage(loaded)
      return loaded
    } catch (error) {
      setFeedback({ kind: 'error', text: error instanceof Error ? error.message : 'Не удалось загрузить очередь.' })
      return null
    }
  }, [])

  useEffect(() => { void loadQueue(offset, statusFilter) }, [loadQueue, offset, statusFilter])

  const [historyEpoch, setHistoryEpoch] = useState(0)

  useEffect(() => {
    if (selectedId === null) { setDetail(null); setHistory(null); return }
    const controller = new AbortController()
    setDetail(null)
    getRecoverySubject(selectedId, controller.signal)
      .then((loaded) => { if (!controller.signal.aborted) setDetail(loaded) })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        setFeedback({ kind: 'error', text: error instanceof Error ? error.message : 'Не удалось загрузить запись.' })
      })
    return () => controller.abort()
  }, [selectedId])

  // Read after every write as well as on open. A settled subject reads only as
  // "closed", so this is the only place its actual outcome and basis exist.
  useEffect(() => {
    if (selectedId === null) return
    const controller = new AbortController()
    getRecoveryDecisionHistory(selectedId, controller.signal)
      .then((loaded) => { if (!controller.signal.aborted) setHistory(loaded.items) })
      .catch(() => { if (!controller.signal.aborted) setHistory(null) })
    return () => controller.abort()
  }, [historyEpoch, selectedId])

  // One idempotency key per subject revision. A retry after a lost response
  // replays the same decision rather than recording a second one; a moved
  // candidate set changes the revision, and with it the key.
  const revisionKey = detail === null ? null : `${detail.id}@${detail.revision}`
  const decisionId = useMemo(() => crypto.randomUUID(), [revisionKey])

  const ranked = useMemo(() => rankCandidates(detail?.candidates ?? []), [detail])
  const indistinguishable = noRecordedFactSeparates(ranked)
  // The legacy row has no weight at all in the contract, so a candidate weight
  // never answers "is this the one". Size answers it only where the subject
  // recorded one.
  const comparability: Comparability = {
    weight: false,
    size: detail !== null && detail.originalProduct.size !== null,
  }
  const held = detail !== null && detail.status === 'held'

  function select(subjectId: string) {
    setSelectedId(subjectId)
    setHistory(null)
    setOutcome(null)
    setTargetProductId(null)
    setBasis(null)
    setNote('')
    setRecorded(null)
    setFeedback(null)
  }

  const request: RecoveryDecisionRequest | null = useMemo(() => {
    const trimmed = note.trim()
    // The server refuses a blank note on every outcome, so the control does too.
    // Letting it through returned a validation body with no Russian sentence,
    // which reached the owner as a generic failure naming nothing.
    if (trimmed === '') return null
    if (outcome === 'matched') {
      if (targetProductId === null || basis === null) return null
      return { outcome: 'matched', targetProductId, basis, evidenceNote: trimmed }
    }
    if (outcome === 'not_any_of_these' || outcome === 'cannot_determine') {
      return { outcome, evidenceNote: trimmed }
    }
    return null
  }, [basis, note, outcome, targetProductId])

  // Nothing can be decided before the stake is on the screen. `detail` is what
  // carries the orders, sales, listings and photographs that move. A held row is
  // one the server refuses, so the screen refuses it first and says why.
  const canDecide = detail !== null && !held && actor.trim() !== '' && request !== null && !busy

  async function submit() {
    if (detail === null || request === null) return
    setBusy(true)
    setFeedback(null)
    try {
      const result = await recordRecoveryDecision(detail.id, decisionId, detail.revision, request)
      setRecorded(result.decision)
      setDetail(result.subject)
      setFeedback({ kind: 'info', text: 'Решение записано.' })
      setHistoryEpoch((value) => value + 1)
      await loadQueue(offset, statusFilter)
    } catch (error) {
      setFeedback({ kind: 'error', text: error instanceof Error ? error.message : 'Решение не записано.' })
    } finally {
      setBusy(false)
    }
  }

  const total = page?.total ?? 0
  const hasPrevious = offset > 0
  const hasNext = offset + PAGE_SIZE < total
  const noteLabel = outcome === 'matched' ? 'Основание решения' : 'Причина'

  return (
    <div className="h-full overflow-y-auto bg-ivory">
      <div className="mx-auto max-w-[1400px] px-3 py-4 pb-[calc(32px+env(safe-area-inset-bottom))] sm:px-5 md:px-7 md:py-6">
        <nav className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
          <Link to="/inventory" className="rounded-md border border-rule bg-paper px-2.5 py-1.5 text-ink-2 hover:bg-bone/60 hover:text-ink">Склад</Link>
          <span aria-hidden="true">/</span>
          <span className="text-ink-2">Восстановление привязки</span>
        </nav>

        <header className="mt-4">
          <h1 className="text-lg font-semibold text-ink">Строки каталога без изделия</h1>
          <p className="mt-1 max-w-[80ch] text-xs text-ink-muted">
            Строка старого каталога совпала по артикулу сразу с несколькими изделиями, и правило
            выбрать между ними не может. Сначала показано состояние изделия, затем вес и размер,
            затем серия и маркировка. Фотография есть примерно у одного изделия из ста, поэтому
            экран построен не на ней.
          </p>
          <p className="mt-1 max-w-[80ch] text-xs text-ink-muted">
            Состояние объясняет кандидата, но само по себе ничего не решает: продажи на площадках в
            1С не записывались. Решение принимает человек или точный идентификатор.
          </p>
        </header>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <label className="text-xs text-ink-muted" htmlFor="recovery-actor">Оператор</label>
          <input
            id="recovery-actor"
            className="rounded-md border border-rule bg-paper px-2 py-1 text-xs text-ink"
            value={actor}
            onChange={(event) => setActor(event.target.value)}
            placeholder="Кто принимает решение"
          />
          <div className="flex flex-wrap gap-1" role="group" aria-label="Состояние очереди">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.label}
                type="button"
                aria-pressed={statusFilter === filter.value}
                className="rounded-md border border-rule px-2 py-1 text-xs"
                onClick={() => { setOffset(0); setStatusFilter(filter.value) }}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {feedback ? (
          <p
            role={feedback.kind === 'error' ? 'alert' : 'status'}
            data-feedback={feedback.kind}
            className="mt-3 rounded-md border border-rule bg-paper px-3 py-2 text-xs text-ink-2"
          >
            {feedback.text}
          </p>
        ) : null}

        {/* Two different populations, said as two different sentences. The open
            count answers for the whole queue and is the number the Warehouse
            button states; the filtered total answers only for the chip in use,
            and names which one produced it. */}
        <p className="mt-3 text-xs text-ink-muted">
          <span data-testid="recovery-open-count">Ждут решения: {(page?.openCount ?? 0).toLocaleString('ru-RU')}</span>
          {' · '}
          <span data-testid="recovery-held-count">ожидают решения владельца: {(page?.heldCount ?? 0).toLocaleString('ru-RU')}</span>
        </p>
        <p className="text-xs text-ink-muted" data-testid="recovery-filtered-total" data-applied-status={page?.appliedStatus ?? undefined}>
          В выбранном списке «{STATUS_FILTERS.find((filter) => filter.value === statusFilter)?.label ?? ''}»: {total.toLocaleString('ru-RU')}
        </p>

        <div className="mt-3 grid gap-4 lg:grid-cols-[minmax(260px,340px)_1fr]">
          <div>
            <ul className="space-y-2" data-testid="recovery-queue">
              {(page?.items ?? []).map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    data-subject={item.id}
                    data-status={item.status}
                    aria-pressed={selectedId === item.id}
                    className={`w-full rounded-lg border p-2 text-left ${selectedId === item.id ? 'border-gold bg-gold/5' : 'border-rule bg-paper'}`}
                    onClick={() => select(item.id)}
                  >
                    <span className="block text-sm font-medium text-ink">{item.article ?? ABSENT_FACT}</span>
                    <span className="block text-xs text-ink-2">{item.title ?? ABSENT_FACT}</span>
                    <span className="mt-1 block text-[11px] text-ink-muted">
                      {subjectStatusLabels[item.status]}
                      {' · '}кандидатов: {item.candidateCount.toLocaleString('ru-RU')}
                    </span>
                    <span className="block text-[11px] text-ink-muted">
                      заказы: {item.attachmentCounts.orderLines.toLocaleString('ru-RU')}
                      {' · '}продажи: {item.attachmentCounts.salesLinks.toLocaleString('ru-RU')}
                      {' · '}объявления: {item.attachmentCounts.listings.toLocaleString('ru-RU')}
                      {' · '}фото: {item.attachmentCounts.media.toLocaleString('ru-RU')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {page !== null && page.items.length === 0 ? (
              <p className="mt-3 text-xs text-ink-muted">В этой части очереди записей нет.</p>
            ) : null}

            <div className="mt-3 flex items-center gap-2">
              <button type="button" className="btn btn-ghost text-xs" disabled={!hasPrevious} onClick={() => setOffset(Math.max(offset - PAGE_SIZE, 0))}>Назад</button>
              <button type="button" className="btn btn-ghost text-xs" disabled={!hasNext} onClick={() => setOffset(offset + PAGE_SIZE)}>Дальше</button>
            </div>
          </div>

          <div>
            {selectedId === null ? (
              <p className="text-xs text-ink-muted">Выберите строку каталога слева.</p>
            ) : detail === null ? (
              <p className="text-xs text-ink-muted" data-testid="recovery-detail-loading">Читаем запись…</p>
            ) : (
              <div data-testid="recovery-detail" data-subject-id={detail.id} data-subject-status={detail.status}>
                <section className="rounded-lg border border-rule bg-paper p-3">
                  <h2 className="text-sm font-semibold text-ink">
                    {detail.originalProduct.article ?? ABSENT_FACT}
                    {' · '}
                    {detail.originalProduct.title ?? ABSENT_FACT}
                  </h2>
                  <p className="mt-1 text-xs text-ink-2">
                    Размер: {detail.originalProduct.size ?? ABSENT_FACT}
                    {' · '}состояние записи: {subjectStatusLabels[detail.status]}
                  </p>
                  {detail.reason === null ? null : (
                    <p className="mt-1 text-xs text-ink-muted" data-reason={detail.reason}>
                      {subjectReasonText(detail.reason)}
                    </p>
                  )}
                  {/* Said here, beside the subject, rather than as a footnote under
                      the candidates. What the subject does not record is what no
                      candidate fact can be measured against. */}
                  <p className="mt-2 text-xs text-ink-2" data-testid="recovery-subject-absent-facts">
                    Чего у этой строки нет: веса — такого поля у строки каталога не существует
                    {detail.originalProduct.size === null ? '; размера — он не записан' : ''}
                    {detail.originalProduct.article === null ? '; артикула — он не записан' : ''}.
                    Поэтому вес{detail.originalProduct.size === null ? ' и размер' : ''} кандидатов
                    отличают кандидатов друг от друга и не показывают, какой из них — эта строка.
                  </p>
                </section>

                {held ? (
                  <p
                    className="mt-3 rounded-md border border-gold/40 bg-gold/5 px-3 py-2 text-xs text-ink-2"
                    role="status"
                    data-testid="recovery-held-notice"
                  >
                    Эта запись уже была объединена с другой. Решение по ней принимает владелец,
                    и сервер отклонит любое решение отсюда. Ниже — что за ней числится и что о ней
                    уже записано.
                  </p>
                ) : null}

                <h3 className="mt-4 text-sm font-semibold text-ink">Что перейдёт вместе с решением</h3>
                <div className="mt-2">
                  <SubjectStake counts={detail.attachmentCounts} attached={detail.attached} />
                </div>

                <h3 className="mt-4 text-sm font-semibold text-ink">
                  Кандидаты: {detail.candidateCount.toLocaleString('ru-RU')}
                </h3>
                {indistinguishable && !held ? (
                  <div className="mt-1" data-testid="recovery-indistinguishable">
                    <p className="text-xs text-ink-muted">
                      Записанные факты не различают этих кандидатов. Если узнать изделие нельзя,
                      выберите «Не могу определить».
                    </p>
                    {/* Reachable without scrolling past the candidates: on a set
                        nothing separates, the undecidable answer is the likely one
                        and must not sit below thirty cards. */}
                    <button
                      type="button"
                      data-testid="recovery-cannot-determine-shortcut"
                      aria-label="Не могу определить: кандидаты неразличимы"
                      className="btn btn-ghost mt-1 text-xs"
                      disabled={busy}
                      onClick={() => { setOutcome('cannot_determine'); setTargetProductId(null); setBasis(null) }}
                    >
                      Не могу определить
                    </button>
                  </div>
                ) : null}
                <ul className="mt-2 space-y-2" data-testid="recovery-candidates">
                  {ranked.map((each) => (
                    <CandidateCard
                      key={each.candidate.productId}
                      ranked={each}
                      comparability={comparability}
                      selected={targetProductId === each.candidate.productId}
                      disabled={busy || held}
                      onSelect={(productId) => { setTargetProductId(productId); setOutcome('matched') }}
                    />
                  ))}
                </ul>

                {held ? null : (
                <section className="mt-4 rounded-lg border border-rule bg-paper p-3" data-testid="recovery-decision-panel">
                  <h3 className="text-sm font-semibold text-ink">Решение</h3>
                  <div className="mt-2 flex flex-wrap gap-1" role="group" aria-label="Исход">
                    {OUTCOMES.map((value) => (
                      <button
                        key={value}
                        type="button"
                        data-outcome-option={value}
                        aria-pressed={outcome === value}
                        className="rounded-md border border-rule px-2 py-1 text-xs"
                        disabled={busy}
                        onClick={() => {
                          setOutcome(value)
                          if (value !== 'matched') { setTargetProductId(null); setBasis(null) }
                        }}
                      >
                        {outcomeLabels[value]}
                      </button>
                    ))}
                  </div>

                  {outcome === 'matched' ? (
                    <div className="mt-3">
                      <p className="text-xs text-ink-muted">На чём основано решение</p>
                      <div className="mt-1 flex flex-wrap gap-1" role="group" aria-label="Основание">
                        {BASES.map((value) => (
                          <button
                            key={value}
                            type="button"
                            data-basis-option={value}
                            aria-pressed={basis === value}
                            className="rounded-md border border-rule px-2 py-1 text-xs"
                            disabled={busy}
                            onClick={() => setBasis(value)}
                          >
                            {basisLabels[value]}
                          </button>
                        ))}
                      </div>
                      <p className="mt-1 text-[11px] text-ink-muted">
                        Узнавание по фотографии остаётся узнаванием навсегда: оно не становится
                        точным фактом источника ни в хранилище, ни в отчёте.
                      </p>
                    </div>
                  ) : null}

                  <div className="mt-3">
                    <label className="text-xs text-ink-muted" htmlFor="recovery-note">
                      {noteLabel}
                    </label>
                    <textarea
                      id="recovery-note"
                      className="mt-1 block w-full rounded-md border border-rule bg-paper px-2 py-1 text-xs text-ink"
                      rows={2}
                      value={note}
                      disabled={busy}
                      onChange={(event) => setNote(event.target.value)}
                    />
                  </div>

                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-ghost text-xs"
                      data-testid="recovery-submit"
                      disabled={!canDecide}
                      onClick={() => void submit()}
                    >
                      Записать решение
                    </button>
                  </div>

                  {recorded === null ? null : <DecisionSummary decision={recorded} />}
                </section>
                )}

                {history === null ? null : <DecisionHistory items={history} />}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
